import {Injectable} from '@angular/core';

import {Observable} from 'rxjs/Observable';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/observable/empty';

import * as io from 'socket.io-client';

import {environment} from '../../../environments/environment';

import {Message} from '../models/message';
import {UtilsService} from './utils.service';

@Injectable()
export class SocketService {
    private debugSocket: boolean = false;

    private socket: SocketIOClient.Socket;

    private observer: Subject<Message> = new BehaviorSubject<Message>(null);

    private _username: string;
    get username(): string {
        return this._username;
    }

    private _roomName: string[] = [];
    set roomName(roomList: string[]) {
        this._roomName = roomList;
    }
    get roomName(): string[] {
        return this._roomName;
    }

    useSocket = environment.common.enable_notifications;  // Use this setting to turn it off during debugging

    constructor(private utilsService: UtilsService) {
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        if (currentUser && currentUser.username) {
            this.login(currentUser.username);
        }
    }

    private initRooms(): void {
        if (this._roomName.length > 0) {
            this._roomName.splice(0, this._roomName.length);
        }
    }

    private createSocket() {
        if (this.useSocket) {
            this.initRooms();
            try {
                if (!this.socket) {
                    this.socket = io.connect(environment.socket_server_url, {
                        'reconnection': true,
                        'reconnectionDelay': 3000,
                        'reconnectionDelayMax': 30000
                    });
                } else {
                    this.socket.disconnect();
                    this.unsetEvents();
                    this.socket.connect();
                }
                this.setEvents();
            } catch (error) {
                this.utilsService.debugLog(error);
            }
        } else {
            this.utilsService.debugLog('Socket disabled programmatically');
        }
    }

    public login(username: string): void {
        if (this.useSocket) {
            this.createSocket();

            this._username = username;
            try {
                this.send('login', this._username);
                this.utilsService.debugLog(`connected to the socket server with username: ${this._username}`);
            } catch (error) {
                this.utilsService.debugLog(error);
            }
        }
    }

    public logout(): void {
        if (this.useSocket && this.socket.connected) {
            this.socket.disconnect();
            this.unsetEvents();
        }
    }

    public message(message: Message): void {
        this.send('message', message);
    }

    public send(event: string, data: any): void {
        if (this.useSocket) {
            try {
                if (event !== 'login' && !this._username) {
                    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
                    if (currentUser && currentUser.username) {
                        this.login(currentUser.username);
                    } else {
                        throw new Error('cannot send a message to socket server without login!');
                    }
                }
                this.debugLog(event, data);
                this.socket.emit(event, data);
            } catch (error) {
                throw error;
            }
        }
    }

    public setEvents() {
        this.socket.on('user joined', (data) => {
            const msg: Message = new Message();
            msg.user = data.username;
            msg.type = 'JOINED';
            msg.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(msg);
            this.observer.next(msg);
        });

        this.socket.on('user left', (data) => {
            const msg: Message = new Message();
            msg.user = data.username;
            msg.type = 'LEFT';
            msg.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(msg);
        });

        this.socket.on('error', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            console.error(data);
            this.observer.next(data);
        });

        this.socket.on('enter cell',(data: Message) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('editing',(data: Message) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('stop editing',(data: Message) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('edited', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('add reg', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('remove reg', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('create group', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('update group', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('remove group', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('add reg to group', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('remove reg from group', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('create market offer', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('load cleared offer', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('confirm cleared quantity', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('no cleared offer', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('set bid period', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('message', (data) => {
            data.timestamp = (data.timestamp === undefined) ? new Date().toUTCString() : data.timestamp;
            this.debugLog(data);
            this.observer.next(data);
        });

        this.socket.on('disconnect', (data) => {
            const msg: Message = new Message();
            msg.user = this._username;
            msg.type = 'DISCONNECTED';
            msg.timestamp = (msg.timestamp === undefined) ? new Date().toUTCString() : msg.timestamp;
            this.utilsService.debugLog('You have been disconnected.');

            this._username = null;

            this.observer.next(msg);
        });

        this.socket.on('reconnect', () => {
            const msg: Message = new Message();
            msg.user = this._username;
            msg.timestamp = (msg.timestamp === undefined) ? new Date().toUTCString() : msg.timestamp;

            const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
            if (currentUser && currentUser.username) {
                try {
                    this.login(currentUser.username);
                    msg.type = 'RECONNECTED';
                    this.utilsService.debugLog('You have been reconnected');
                } catch (error) {
                    msg.type = 'ERROR';
                    this.utilsService.debugLog(error);
                }
            }
            // if (this._username) {
            //     try {
            //         this.send('login', this._username);
            //         this.utilsService.debugLog('You have been reconnected');
            //     } catch (error) {
            //         this.utilsService.debugLog(error);
            //     }
            // }

            this.observer.next(msg);
        });

        this.socket.on('reconnect_error', (data) => {
            this.utilsService.debugLog('Could not reconnect with a socket');
            this.observer.next(data);
        });

        this.socket.on('message', (msg: Message) => {
            msg.timestamp = (msg.timestamp === undefined) ? new Date().toUTCString() : msg.timestamp;
            this.debugLog(msg);
            this.observer.next(msg);
        });
    }

    public unsetEvents() {
        this.socket.removeAllListeners();
    }

    public onMessage(): Observable<Message> {
        // Observable.empty : not to publish a message
        return (this.useSocket) ? this.observer.asObservable() : Observable.empty<Message>();
    }

    private debugLog(...args: any[]) {
        if (this.debugSocket) {
            this.utilsService.debugLog.apply(console, args);
        }
    }
}


// WEBPACK FOOTER //
// ./src/app/core/services/socket.service.ts